import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SectionHeading } from "../common/SectionHeading";
import { Reveal } from "../common/Reveal";
import { api } from "../../lib/api";

const PRODUCT_TYPES = [
    { key: "cotton_candy", label: "Cotton Candy Flavors" },
    { key: "popcorn", label: "Popcorn Flavors" },
];

export const FlavorStage = () => {
    const [flavors, setFlavors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [productType, setProductType] = useState("cotton_candy");
    const [active, setActive] = useState("");

    useEffect(() => {
        api.get("/flavors").then((res) => setFlavors(res.data || [])).catch(() => {});
        api.get("/flavor-categories").then((res) => setCategories(res.data || [])).catch(() => {});
    }, []);

    const productCats = useMemo(() => categories.filter((c) => c.product_type === productType), [categories, productType]);

    useEffect(() => {
        setActive(productCats[0]?.name || "");
    }, [productCats]);

    const visible = flavors.filter((f) => f.product_type === productType && f.category === active);

    return (
        <section data-testid="flavor-stage-section" className="bg-ink tent-stripes text-white py-24 md:py-32 overflow-hidden">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <SectionHeading
                    chapter="03"
                    kicker="The Menu"
                    title="Flavor Takes Center Stage."
                    copy="Two catalogs. Zero overlap. Pick your treat, then pick your flavor."
                    dark
                    testid="flavor-stage-heading"
                />
                <Reveal>
                    <div className="grid sm:grid-cols-2 gap-3 mb-10" data-testid="flavor-product-toggle">
                        {PRODUCT_TYPES.map((pt) => (
                            <button
                                key={pt.key}
                                onClick={() => setProductType(pt.key)}
                                data-testid={`flavor-product-${pt.key}`}
                                className={`rounded-3xl px-6 py-6 text-left transition-all duration-300 active:scale-[0.98] ${
                                    productType === pt.key
                                        ? "bg-pink text-ink"
                                        : "border border-zinc-700 text-zinc-300 hover:border-pink hover:text-pink"
                                }`}
                            >
                                <span className="font-oswald uppercase text-2xl md:text-3xl block">{pt.label}</span>
                            </button>
                        ))}
                    </div>
                    <div data-testid="flavor-tabs" className="flex flex-wrap gap-2 mb-12">
                        {productCats.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setActive(cat.name)}
                                data-testid={`flavor-tab-${cat.name.toLowerCase()}`}
                                className={`rounded-full px-5 py-2.5 font-oswald uppercase tracking-widest text-xs transition-colors duration-300 active:scale-95 ${
                                    active === cat.name
                                        ? "bg-pink text-ink"
                                        : "border border-zinc-700 text-zinc-300 hover:border-pink hover:text-pink"
                                }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </Reveal>
                <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] items-start">
                    <AnimatePresence mode="wait">
                        <motion.p
                            key={active}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -24 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="flavor-category-title"
                            className="font-oswald uppercase leading-[0.85] text-[18vw] lg:text-[7.5rem] text-stroke-pink select-none"
                        >
                            {active || "SOON"}
                        </motion.p>
                    </AnimatePresence>
                    <AnimatePresence mode="wait">
                        <motion.ul
                            key={`${productType}-${active}`}
                            initial="hidden"
                            animate="show"
                            exit={{ opacity: 0, transition: { duration: 0.2 } }}
                            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                            className="divide-y divide-zinc-800 border-y border-zinc-800"
                            data-testid="flavor-list"
                        >
                            {visible.map((f) => (
                                <motion.li
                                    key={f.id}
                                    variants={{ hidden: { opacity: 0, x: 24 }, show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } }}
                                    className="group py-5 flex items-center gap-5"
                                >
                                    {f.image ? (
                                        <img src={f.image} alt={f.name} className="h-12 w-12 shrink-0 rounded-full object-cover border border-white/20 transition-transform duration-300 group-hover:scale-110" />
                                    ) : (
                                        <span
                                            className="h-8 w-8 shrink-0 rounded-full border border-white/20 transition-transform duration-300 group-hover:scale-125"
                                            style={{ background: f.color }}
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-oswald uppercase text-xl md:text-2xl group-hover:text-pink transition-colors duration-300">
                                            {f.name}
                                            {f.seasonal && <span className="ml-3 align-middle text-[9px] tracking-[0.3em] text-pink border border-pink rounded-full px-2 py-0.5">SEASONAL</span>}
                                        </p>
                                        <p className="text-sm text-zinc-400">{f.description}</p>
                                    </div>
                                </motion.li>
                            ))}
                            {visible.length === 0 && (
                                <li className="py-8 text-zinc-500 font-oswald uppercase tracking-widest text-sm">New flavors spinning up for this category.</li>
                            )}
                        </motion.ul>
                    </AnimatePresence>
                </div>
                <Reveal delay={0.1}>
                    <Link
                        to="/flavors"
                        data-testid="explore-flavors-cta"
                        className="mt-12 inline-flex items-center gap-2 rounded-full bg-pink px-8 py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors duration-300 active:scale-95"
                    >
                        Explore Flavors <ArrowRight size={16} />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
};
